import api from './api';

// ---------------------------------------------------------------------------
// Data Privacy API Service (Phase 9)
// ---------------------------------------------------------------------------

export interface ConsentRecord {
  id: string;
  userId: string;
  consentType: 'terms' | 'privacy' | 'marketing' | 'data_processing';
  granted: boolean;
  grantedAt: string | null;
  revokedAt: string | null;
  version: string;
  ipAddress: string | null;
  userAgent: string | null;
}

export interface DataSubjectRequest {
  id: number;
  userId: string;
  requestType: 'access' | 'erasure' | 'rectification' | 'portability';
  status: 'pending' | 'processing' | 'completed' | 'denied';
  notes: string | null;
  requestedAt: string;
  completedAt: string | null;
  completedBy: string | null;
  responseData: unknown;
  userEmail?: string;
  firstName?: string;
  lastName?: string;
}

// ── Consent ──────────────────────────────────────────────────────────────

export async function getMyConsents(): Promise<ConsentRecord[]> {
  const res = await api.get('/data-privacy/consent');
  return res.data?.data?.consents ?? [];
}

export async function grantConsent(
  consentType: string,
  version: string,
): Promise<ConsentRecord> {
  const res = await api.post('/data-privacy/consent', { consentType, version });
  return res.data?.data;
}

export async function revokeConsent(consentType: string): Promise<void> {
  await api.delete(`/data-privacy/consent/${consentType}`);
}

// ── Data Subject Requests ────────────────────────────────────────────────

export async function getMyRequests(): Promise<DataSubjectRequest[]> {
  const res = await api.get('/data-privacy/requests');
  return res.data?.data?.requests ?? [];
}

export async function submitRequest(
  requestType: string,
  notes?: string,
): Promise<DataSubjectRequest> {
  const res = await api.post('/data-privacy/requests', { requestType, notes });
  return res.data?.data;
}

// ── Admin ────────────────────────────────────────────────────────────────

export async function adminListRequests(
  status?: 'pending' | 'processing' | 'completed' | 'denied',
): Promise<DataSubjectRequest[]> {
  const res = await api.get('/data-privacy/admin/requests', {
    params: status ? { status } : undefined,
  });
  return res.data?.data?.requests ?? [];
}

export async function adminProcessRequest(id: number): Promise<DataSubjectRequest> {
  const res = await api.post(`/data-privacy/admin/requests/${id}/process`);
  return res.data?.data;
}

export async function adminDenyRequest(
  id: number,
  reason: string,
): Promise<DataSubjectRequest> {
  const res = await api.post(`/data-privacy/admin/requests/${id}/deny`, { reason });
  return res.data?.data;
}
